/**
 * Pure presentation helpers for the live conversation pane (pane B). No
 * component code, no store/ops access — see docs/design-handoff/README.md
 * "3. Live conversation (pane B)" for the spec this follows, mirroring the
 * split `src/components/rail/format.ts` already established for pane A.
 */
import type { CSSProperties } from 'react'
import type { CallState, Channel, Liveness, Message, Tier, ThreadState } from '@communicator/shared'
import type { ServiceStatus } from '../../store'
import type { IconName } from '../ui'

/**
 * `selfId` is the signed-in staff member's real id (`store.me?.id`,
 * task 27 — see src/lib/identity.ts) — `null` when identity has not resolved
 * yet. Previously this pair compared against a fixed `'me'` sentinel lifted
 * from the design prototype's seed-data convention (task-13-report.md
 * decision 3, task-15-report.md decision 1); that was a placeholder, not a
 * real id, and would never have matched a real `staff.id` (a UUID) once this
 * console talks to the actual backend. Callers now pass the real value (or
 * `null`) explicitly — this module stays a pure formatting helper with no
 * store access of its own, matching the rest of this file.
 *
 * `selfId === null` (identity not established) can never own anything: that
 * is the honest reading (we cannot verify a match we have no value for), and
 * it is also exactly the same effective behaviour every caller already had
 * before this file knew about real identity at all.
 */
export function isOwnedByMe(ownerId: string | null, selfId: string | null): boolean {
  return selfId !== null && ownerId === selfId
}

/** True only when someone else actively holds the thread — an unowned thread is not "someone else's". */
export function isOwnedBySomeoneElse(ownerId: string | null, selfId: string | null): boolean {
  return ownerId !== null && ownerId !== selfId
}

export const TRANSPORT_KEY_FOR_CHANNEL: Record<Channel, keyof ServiceStatus> = {
  chat: 'relay',
  sms: 'sms',
  voice: 'voice',
}

export function isTransportDown(channel: Channel, svc: ServiceStatus): boolean {
  return !svc[TRANSPORT_KEY_FOR_CHANNEL[channel]]
}

/**
 * The thread header's tier badge — Tossker repaint (task 5), README §4: "a
 * pill with an icon (`shield-check` + 'Verified' on `--status-success-soft`;
 * `user` + 'Matched' on `--teal-50`; dashed `--gold-600` border when
 * Unresolved)." Unknown/ambiguous both count as "Unresolved" for styling —
 * the README gives one dashed-border treatment for the pair, not two — but
 * the label text keeps them distinct (Unknown vs Ambiguous) rather than
 * collapsing both to the single word "Unresolved", so an operator scanning
 * the header does not lose which case they are looking at.
 *
 * Superseded the old ALL-CAPS `TIER_BADGE_LABEL`/`TIER_BADGE_TONE` +
 * generic `Chip` rendering — this badge needs its own icon and its own
 * dashed-border case, neither of which `Chip` (rail-only, tone-driven)
 * supports.
 */
export const TIER_BADGE_ICON: Record<Tier, IconName> = {
  unknown: 'user',
  ambiguous: 'user',
  matched: 'user',
  otp: 'shield-check',
  verified: 'shield-check',
}

export const TIER_BADGE_TEXT: Record<Tier, string> = {
  unknown: 'Unknown',
  ambiguous: 'Ambiguous',
  matched: 'Matched',
  otp: 'OTP-verified',
  verified: 'Verified',
}

const UNRESOLVED_TIER_BADGE_STYLE: CSSProperties = {
  background: 'var(--tint-accent)',
  color: 'var(--on-tint-accent)',
  border: '1px dashed var(--brand-accent)',
}

/**
 * Verified/OTP-verified: `--status-success-soft` fill. Matched: `--tint-primary`
 * (teal-50 in Tossker Day). Unknown/ambiguous: the dashed-gold "Unresolved"
 * treatment above. All three are semantic-token `var()` references, never a
 * literal — consumed as an inline `style`, the same established pattern
 * `CallFloor.tsx`/`RailRow.tsx` use throughout this repaint for colours with
 * no dedicated Tailwind utility.
 */
export function tierBadgeStyle(tier: Tier): CSSProperties {
  if (tier === 'verified' || tier === 'otp') {
    return { background: 'var(--status-success-soft)', color: 'var(--status-success)' }
  }
  if (tier === 'matched') {
    return { background: 'var(--tint-primary)', color: 'var(--brand-primary-text)' }
  }
  return UNRESOLVED_TIER_BADGE_STYLE
}

export function isUnresolvedTier(tier: Tier): boolean {
  return tier === 'unknown' || tier === 'ambiguous'
}

/** blue live / green online / text-lo async — README §3. Ringing reuses live's urgency; "over" reuses async's quiet. */
export function presenceDotStyle(liveness: Liveness): { className: string; style?: CSSProperties } {
  switch (liveness) {
    case 'live':
    case 'ringing':
      return { className: 'bg-cc-blue' }
    case 'online':
      return { className: 'bg-ok' }
    case 'async':
    case 'over':
    default:
      // No dedicated "bg-lo" utility exists (text-lo is text-only) — this
      // references the same --cc-text-lo custom property tokens.css already
      // declares, just as a background instead of a new hex.
      return { className: '', style: { backgroundColor: 'var(--cc-text-lo)' } }
  }
}

/**
 * The "Send a code" button's exact green treatment is given literally in
 * docs/design-handoff/README.md §3 (a specific border alpha, a specific fill
 * alpha, and the design system's plain `ok` green for the text) but
 * src/styles/tokens.css and preset.ts sit outside this task's scope path, so
 * those exact values cannot become new Tailwind utility classes here.
 * `color-mix()` against the existing `--cc-ok` custom property reproduces
 * the same alpha-blended colours with zero hardcoded hex anywhere in this
 * file — see task-15-report.md for the full rationale, including the exact
 * alpha values this reproduces.
 *
 * `OUTBOUND_BUBBLE_BORDER` (the old alpha-blended outbound-bubble border)
 * is gone as of task 5 (Tossker repaint, README §4): the outbound bubble is
 * now a SOLID `--brand-primary` fill with white text, not a pale tint with a
 * blended border — see Transcript.tsx.
 */
export const SEND_CODE_BORDER = 'color-mix(in srgb, var(--cc-ok) 55%, transparent)'
export const SEND_CODE_FILL = 'color-mix(in srgb, var(--cc-ok) 12%, transparent)'
export const SEND_CODE_TEXT = 'var(--cc-ok)'

export function sendCodeStyle(tier: Tier): CSSProperties {
  return {
    borderColor: SEND_CODE_BORDER,
    backgroundColor: SEND_CODE_FILL,
    color: SEND_CODE_TEXT,
    opacity: tier === 'matched' ? 1 : 0.55,
  }
}

/**
 * Builds the Halo ticket deep link from config, never a literal host.
 * `h.endpoint` (CONFIG_FIELDS in packages/shared/src/config.ts) is the API
 * base (".../api"); the web ticket URL lives at the same host without that
 * suffix. No dedicated "Halo web base" config field exists yet — this is
 * the closest config-sourced value available, documented as a decision in
 * task-15-report.md.
 *
 * Returns `null` — never a guess — when `h.endpoint` is missing or blank
 * (task-42, bug 1: before this, a missing endpoint built the *relative*
 * URL `/ticket?id=…`, which the browser resolves against the console's own
 * origin; the owner clicked a ticket chip and landed on the console
 * instead of Halo). Callers must treat `null` as "cannot build this link
 * yet" and render something non-clickable rather than a plausible-looking
 * wrong href — see ThreadHeader.tsx / ReadOnlyTranscript.tsx.
 *
 * `<page>?id=<n>` is UNVERIFIED against a real Halo URL — Halo's own
 * `bookingurl` field on a ticket carries the shape `/booking?id=<n>&key=…`,
 * which is consistent with a `<page>?id=<n>` convention, but `ticket` as
 * the specific page name has not been confirmed live. Do not change it to a
 * different guess; get a real Halo ticket URL and confirm it first.
 */
export function haloTicketUrl(cfg: Record<string, unknown>, ticket: string): string | null {
  const raw = typeof cfg['h.endpoint'] === 'string' ? (cfg['h.endpoint'] as string).trim() : ''
  if (!raw) return null
  const base = raw.replace(/\/api\/?$/i, '')
  const id = ticket.replace(/^#/, '')
  return `${base}/ticket?id=${encodeURIComponent(id)}`
}

export function formatClock(elapsedS: number): string {
  const s = Math.max(0, Math.floor(elapsedS))
  const m = Math.floor(s / 60)
  const sec = s % 60
  return `${m}:${sec.toString().padStart(2, '0')}`
}

/** Placeholder varies by channel and liveness — README §3. No verbatim copy is given, so this is original UI text. */
export function composerPlaceholder(channel: Channel, liveness: Liveness): string {
  if (channel === 'chat') {
    return liveness === 'online' ? "They're online — send a message…" : "Send a message — they'll see it when they're back…"
  }
  if (channel === 'sms') {
    return liveness === 'async' ? 'Send a text — delivery may take a moment…' : 'Send a text…'
  }
  return 'Add a note about this call…'
}

interface OwnershipLike {
  state: ThreadState
  ownerId: string | null
  owner: string | null
}

/**
 * Resolution #4: a read-only thread is one you do not own (actively owned by
 * someone else — an unclaimed thread stays writable, since sending is how a
 * dispatcher claims it) or a closed one. Returns null when the thread is
 * writable. `selfId` is the signed-in staff member's real id — see
 * `isOwnedBySomeoneElse` above.
 */
export function readOnlyReason(thread: OwnershipLike, selfId: string | null): string | null {
  if (thread.state === 'closed') {
    return 'This thread is closed. Closed is terminal — nothing more can be sent here.'
  }
  if (isOwnedBySomeoneElse(thread.ownerId, selfId)) {
    return `You can read this thread, but only ${thread.owner ?? 'its owner'} can reply here.`
  }
  return null
}

/**
 * The header's single close verb — task 5 (Tossker repaint), README §4 and
 * "Open questions for the PR" item 1. Replaces `closeButtonLabel()`, which
 * used to switch both the label ("Close" with a ticket, "Dismiss" without)
 * AND the button's variant (`primary` with a ticket, `danger` without —
 * "Dismiss" on an unticketed thread was coloured like a destructive act,
 * because it leaves no record).
 *
 * Andrew's explicit call (task-5-brief.md binding decision 2): one verb,
 * ALWAYS primary, no danger variant, and no confirm step added to
 * compensate for losing the warning colour. The variant branch is deleted
 * outright, not hidden behind a flag — `ThreadHeader` now renders a single
 * `<Button variant="primary">` regardless of `thread.ticket`.
 */
export const RESOLVE_LABEL = 'Resolve'

/**
 * Voice suppresses the composer entirely, in every call state.
 *
 * It used to suppress only while RINGING or LIVE, so the composer reappeared
 * the moment a call ended — which is precisely when someone reading the thread
 * is most likely to type into it. There is no send for voice at all: the
 * adapter throws, because 3CX owns the audio and this system never touches it.
 * A composer you can type into and never send from is worse than none, and a
 * greyed-out one still reads as "possible under some condition".
 *
 * SUPERSEDED — see the body. That reasoning was about SENDING, and it still
 * holds: there is no voice send. It was wrongly applied to typing itself,
 * which left a dispatcher on a live call with nowhere to write anything.
 */
export function isComposerSuppressed(_channel: Channel, _callState: CallState | null | undefined): boolean {
  // NOTHING IS SUPPRESSED ANY MORE.
  //
  // This returned true for every voice thread, and the reasoning above was
  // half right: there is no SEND for voice, because 3CX owns the audio and
  // the adapter throws. But that was conflated with "there is nothing to
  // type", and they are not the same thing. A dispatcher on a live call had
  // nowhere to write anything at all — which is the gap this feature exists
  // to close.
  //
  // The composer on a voice thread offers two modes: a note, which goes to
  // ourselves, and a text, which goes to the caller's number by the same
  // route `request-otp` already uses. Neither of them is a voice send, so
  // the original rule is intact — it just never meant this.
  //
  // Kept as a function, with both arguments, because the honest expectation
  // is that some future state DOES need to suppress it (a closed thread
  // already routes through `readOnlyReason` instead), and a caller that has
  // stopped passing the state cannot start again cheaply.
  return false
}

/** Sender/time meta shows only above the first bubble of a consecutive run from the same sender. */
export function isRunStart(messages: Message[], index: number): boolean {
  if (index === 0) return true
  const prev = messages[index - 1]
  const cur = messages[index]
  return prev.from !== cur.from || prev.author !== cur.author
}

/**
 * True when nobody holds the thread at all.
 *
 * The header used to offer "Take over from <name>" and nothing else, so an
 * UNCLAIMED thread had no claim affordance — while `gates.ts` refuses every
 * non-exempt operation unless `thread.ownerId === actor.id`. A tech opening an
 * unowned conversation was told "Claim it first" with nothing to click.
 *
 * It stayed hidden because dispatch and admins have reach-in (gates.ts's
 * `reachIn`) and never meet the refusal; it surfaced the first time a plain
 * tech signed in.
 */
export function isUnclaimed(ownerId: string | null): boolean {
  return ownerId === null
}

/**
 * Two-letter initials from a display name — task 5 (Tossker repaint).
 * Centralised here rather than left duplicated: `ThreadHeader.tsx` carried
 * its own private copy before this task, and `LiveCallStrip.tsx` needs the
 * identical logic now that it renders a "You · ext NNN" chip (README §4's
 * voice live-call bar). Deliberately NOT re-exported from
 * `../rail/format.ts`'s own `initialsOf` — that module is pane A's and this
 * is pane B's, and the two directories stay decoupled on purpose (see this
 * file's own `formatClock` vs. `../rail/format.ts`'s `formatFloorClock` for
 * the same non-coupling already established in this repaint).
 */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  const first = parts[0]!
  const last = parts[parts.length - 1]!
  return parts.length > 1 ? (first[0]! + last[0]!).toUpperCase() : first.slice(0, 2).toUpperCase()
}

/**
 * Message bubble corners — task 5 binding decision 1: "radius 16 with a 4px
 * tail corner on the sender's side." `rounded-bubble` (16px, preset.ts) on
 * three corners, `4px` on the fourth — as four independent per-corner
 * classes rather than a base `rounded-bubble` plus one override, because
 * mixing a general `rounded-*` utility with a corner-specific one depends on
 * Tailwind's generated stylesheet ordering to win the cascade; four
 * unambiguous corner classes have no such dependency.
 *
 * Inbound tails bottom-left (README §4: "radius 16 16 16 4"); outbound tails
 * bottom-right ("radius 16 16 4 16").
 */
export function bubbleCornerClasses(outbound: boolean): string {
  return outbound
    ? 'rounded-tl-bubble rounded-tr-bubble rounded-bl-bubble rounded-br-[4px]'
    : 'rounded-tl-bubble rounded-tr-bubble rounded-br-bubble rounded-bl-[4px]'
}

/**
 * The finished-call summary's "6:42 → 15m" rounding chip (README §4's B2
 * frame) — the actual/billed-minutes pair for a call whose time has not yet
 * been logged. Rounds UP to the nearest 15-minute increment, minimum 15m for
 * any answered call, matching the standard MSP billing-increment convention
 * this console's own Halo time entries already follow (see `log-call`'s own
 * server-side write). Purely informational: `CallSummary`'s `Log call`
 * (`log-call` op) still takes only `note`/`billable`/`attachRecording` — this
 * label is never sent anywhere, only shown. Returns `null` for a call with no
 * billable duration to round (missed, or not yet answered).
 */
export function roundedBillingLabel(durationS: number | null): string | null {
  if (durationS === null || durationS <= 0) return null
  const minutes = Math.max(15, Math.ceil(durationS / 900) * 15)
  return `${formatClock(durationS)} → ${minutes}m`
}

/**
 * The device's own timezone, per `Intl.DateTimeFormat().resolvedOptions()`.
 * Wrapped in a `try` purely as a last resort — an engine with no `Intl`
 * support at all is not one this console otherwise runs on, but a thrown
 * error from a formatter over a cosmetic value is the wrong failure mode
 * regardless, matching `packages/shared/src/time.ts`'s own rule of never
 * taking a renderer down over a timestamp.
 */
function deviceTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return 'UTC'
  }
}

/**
 * Resolves the stored `p.tz` preference to a real IANA zone `Intl` will
 * accept. `'auto'` — the pref's own default (2026-08-24 design, "a fresh
 * account is correct without ever opening Settings") — always means the
 * device's own zone. Anything else is handed straight to `Intl` as a probe:
 * a valid IANA name passes through unchanged, and an invalid one (a typo, a
 * name from an older options list, direct API misuse) falls back to auto
 * rather than throwing — the same "never take a renderer down over a
 * timestamp" rule `partsInZone` in packages/shared/src/time.ts already
 * follows for the org-wide `v.tz` setting.
 */
function resolveMessageTimeZone(tz: string): string {
  if (tz === 'auto') return deviceTimeZone()
  try {
    // Probe only — Intl throws synchronously on construction for an
    // unrecognised zone, before anything is formatted.
    new Intl.DateTimeFormat('en-US', { timeZone: tz })
    return tz
  } catch {
    return deviceTimeZone()
  }
}

/**
 * "Aug 24, 10:22 PM" — the console's one human-facing message timestamp,
 * 2026-08-24 design ("the messages are all showing in UTC, which makes no
 * sense to humans"). `tz` is the viewer's stored `p.tz` preference (or
 * `'auto'`), never read from anywhere ambient here — this stays a pure
 * function like the rest of this file.
 *
 * The year is included only when `iso` is not from the current year (in the
 * resolved zone) — a transcript read the same week it happened doesn't need
 * one, a transcript read next spring does.
 *
 * An unparsable or empty `iso` is returned unchanged rather than formatted:
 * `Transcript.tsx`'s own test fixtures pass a bare clock-time string like
 * `'11:04'` (not a full ISO instant) for tests that don't care about the
 * pref, and this must not turn that into "Invalid Date" — see
 * `packages/shared/src/time.ts`'s `partsInZone` for the same rule stated
 * there.
 */
export function formatMessageStamp(iso: string, tz: string): string {
  if (!iso) return iso
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso

  const zone = resolveMessageTimeZone(tz)
  const yearOf = (date: Date) => new Intl.DateTimeFormat('en-US', { timeZone: zone, year: 'numeric' }).format(date)
  const includeYear = yearOf(d) !== yearOf(new Date())

  return new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    ...(includeYear ? { year: 'numeric' as const } : {}),
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(d)
}
